import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const DAYS = [1, 7, 30];
const fmt = (s) => new Date(s).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

function BlockForm({ g, busy, onSubmit, onCancel }) {
  const [days, setDays] = useState(7);
  const [reason, setReason] = useState("");
  const ok = reason.trim().length >= 3;
  return (
    <div className="mt-2 space-y-2 rounded-2xl bg-red-50/60 p-3" data-testid={`game-block-form-${g.id}`}>
      <div className="flex gap-1.5">
        {DAYS.map((d) => (
          <button key={d} type="button" onClick={() => setDays(d)} data-testid={`game-block-days-${g.id}-${d}`}
            className={`px-3 h-8 rounded-full text-xs font-bold transition-colors ${days === d ? "bg-red-600 text-white" : "bg-white text-red-600 border border-red-200"}`}>{d} {d === 1 ? "Tag" : "Tage"}</button>
        ))}
      </div>
      <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} placeholder="Grund (Pflicht) – z. B. Beleidigung im Team-Chat" className="rounded-xl bg-white" data-testid={`game-block-reason-${g.id}`} />
      <div className="flex gap-2">
        <button disabled={!ok || busy} onClick={() => onSubmit(days, reason.trim())} data-testid={`game-block-confirm-${g.id}`} className="px-4 h-9 rounded-full bg-red-600 text-white text-xs font-bold disabled:opacity-50">Sperren</button>
        <button onClick={onCancel} data-testid={`game-block-cancel-${g.id}`} className="px-4 h-9 rounded-full bg-white border border-slate-200 text-xs font-semibold">Abbrechen</button>
      </div>
    </div>
  );
}

function GameRow({ g, info, isBlocked, busy, onBlock, onUnblock }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="py-3" data-testid={`game-block-row-${g.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className={`text-sm font-semibold ${isBlocked ? "text-red-600" : "text-slate-700"}`}>{g.name}</p>
          {isBlocked && <p className="text-[11px] text-slate-500" data-testid={`game-block-info-${g.id}`}>{info?.until ? `gesperrt bis ${fmt(info.until)}` : "gesperrt"}{info?.reason && ` · ${info.reason}`}</p>}
        </div>
        {isBlocked
          ? <button disabled={busy} onClick={onUnblock} data-testid={`game-unblock-${g.id}`} className="shrink-0 px-3 h-8 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold disabled:opacity-50">Freigeben</button>
          : !open && <button onClick={() => setOpen(true)} data-testid={`game-block-${g.id}`} className="shrink-0 px-3 h-8 rounded-full bg-red-50 text-red-600 text-xs font-bold">Sperren</button>}
      </div>
      {!isBlocked && open && <BlockForm g={g} busy={busy} onSubmit={async (d, r) => { if (await onBlock(d, r)) setOpen(false); }} onCancel={() => setOpen(false)} />}
    </li>
  );
}

export function GameBlocksDialog({ u, onClose, onChange }) {
  const [games, setGames] = useState([]);
  const [state, setState] = useState({ blocks: u.game_blocks || [], info: u.game_block_info || {} });
  const [busy, setBusy] = useState(null);
  useEffect(() => { api.get("/admin/game-blocks").then(({ data }) => setGames(data)).catch(() => {}); }, []);
  const send = async (g, body, msg) => {
    setBusy(g.id);
    try {
      const { data } = await api.put(`/admin/users/${u.user_id}/game-blocks/${g.id}`, body);
      setState({ blocks: data.game_blocks, info: data.game_block_info });
      onChange(data.game_blocks, data.game_block_info);
      toast.success(msg);
      return true;
    } catch (e) { toast.error(formatErr(e)); return false; } finally { setBusy(null); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-md max-h-[90dvh] overflow-y-auto" data-testid="game-blocks-dialog">
        <DialogTitle className="flex items-center gap-2"><Lock className="w-5 h-5 text-red-500" /> Spiele sperren – {u.name}</DialogTitle>
        <DialogDescription>Sperre für 1, 7 oder 30 Tage mit Grund. {u.name} bekommt eine Mitteilung mit dem Grund – und eine weitere, wenn die Sperre automatisch endet.</DialogDescription>
        <ul className="divide-y divide-pink-50">
          {games.map((g) => (
            <GameRow key={g.id} g={g} info={state.info[g.id]} isBlocked={state.blocks.includes(g.id)} busy={busy === g.id}
              onBlock={(days, reason) => send(g, { blocked: true, days, reason }, `${u.name} ist ${days} ${days === 1 ? "Tag" : "Tage"} für „${g.name}“ gesperrt`)}
              onUnblock={() => send(g, { blocked: false }, `„${g.name}“ für ${u.name} freigegeben`)} />
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
